import axiosInstance from '@/api/axiosInstance';
import { buildQueryString } from '@/utils/query';
import { LEARNING_PATH_ENDPOINTS } from '@/api/endpoints/learning-path.endpoints';
import {
  LearningPath, LearningPathCreate, LearningPathUpdate, LearningPathAssignment, LearningPathAssignmentRead, AssignPreview, BackendAssignmentType, LearningPathProgress,
  CertificationCycle, LearningPathLearnerView, ProgramReportRow,
  LearningPathRatingCreate, LearningPathRatingItem, PaginatedLearningPathRatingsResponse,
  LearningPathResource, LearningPathResourceCreate, LearningPathResourceUpdate,
  ProgramEnrolmentMethod, ProgramEnrolMethodType, ProgramLearnerRow, ProgramEnrolmentReportRow,
  ProgramLearnerCompletionResult, CertificationReportRow,
} from '@/types/learning-path.types';
import { ManagementApiService, FormApiService } from '@/types/management.types';

export const LearningPathManagementApiService: ManagementApiService<LearningPath, {}> = {
  getAll: async (offset = 0, limit = 20) => {
    const query = buildQueryString({ offset, limit });
    const items = (await axiosInstance.get<LearningPath[]>(`${LEARNING_PATH_ENDPOINTS.LEARNING_PATHS}${query}`)).data;
    return {
      items,
      total: items.length < limit ? offset + items.length : offset + items.length + 1,
      limit,
      offset,
    };
  },
  deleteItem: async (id: number): Promise<void> => {
    await axiosInstance.delete(LEARNING_PATH_ENDPOINTS.LEARNING_PATH_DETAIL(id));
  },
};

export const LearningPathFormApiService: FormApiService<LearningPath, LearningPathCreate, LearningPathUpdate> = {
  getItem: async (id: number | string): Promise<LearningPath> =>
    (await axiosInstance.get<LearningPath>(LEARNING_PATH_ENDPOINTS.LEARNING_PATH_DETAIL(Number(id)))).data,
  createItem: async (data: LearningPathCreate): Promise<LearningPath> =>
    (await axiosInstance.post<LearningPath>(LEARNING_PATH_ENDPOINTS.LEARNING_PATHS, data)).data,
  updateItem: async (id: number, data: LearningPathUpdate): Promise<LearningPath> =>
    (await axiosInstance.put<LearningPath>(LEARNING_PATH_ENDPOINTS.LEARNING_PATH_DETAIL(id), data)).data,
};

export const LearningPathApi = {
  Management: LearningPathManagementApiService,
  Form: LearningPathFormApiService,
  listLearningPaths: async (offset = 0, limit = 20): Promise<LearningPath[]> => {
    const query = buildQueryString({ offset, limit });
    return (await axiosInstance.get<LearningPath[]>(`${LEARNING_PATH_ENDPOINTS.LEARNING_PATHS}${query}`)).data;
  },
  getLearningPath: LearningPathFormApiService.getItem!,
  createLearningPath: LearningPathFormApiService.createItem,
  updateLearningPath: LearningPathFormApiService.updateItem,
  deleteLearningPath: LearningPathManagementApiService.deleteItem,
  assignLearningPath: async (data: LearningPathAssignment): Promise<{ message: string }> =>
    (await axiosInstance.post<{ message: string }>(LEARNING_PATH_ENDPOINTS.ASSIGN, data)).data,

  listAssignments: async (id: number | string): Promise<LearningPathAssignmentRead[]> =>
    (await axiosInstance.get<LearningPathAssignmentRead[]>(LEARNING_PATH_ENDPOINTS.ASSIGNMENTS(id))).data,

  /** Who a selection resolves to: unique learners, already enrolled, per target, members. */
  previewAssignment: async (
    pathId: number | string,
    targets: { assignment_type: BackendAssignmentType; target_id: number }[],
  ): Promise<AssignPreview> =>
    (await axiosInstance.post<AssignPreview>(LEARNING_PATH_ENDPOINTS.ASSIGN_PREVIEW(pathId), { targets })).data,

  /** Change one assignment's due rule (a date, a relative one, or none). */
  updateAssignmentDue: async (
    assignmentId: number | string,
    due: Pick<LearningPathAssignment, 'due_date' | 'due_date_relative_days' | 'due_date_relative_unit' | 'due_date_hr_event'>,
  ): Promise<void> => {
    await axiosInstance.patch(LEARNING_PATH_ENDPOINTS.ASSIGNMENT(assignmentId), due);
  },

  deleteAssignment: async (assignmentId: number | string): Promise<void> => {
    await axiosInstance.delete(LEARNING_PATH_ENDPOINTS.DELETE_ASSIGNMENT(assignmentId));
  },

  /** Every learner in every program / certification (Program enrolments report). */
  getEnrolmentsReport: async (): Promise<ProgramEnrolmentReportRow[]> =>
    (await axiosInstance.get<ProgramEnrolmentReportRow[]>(LEARNING_PATH_ENDPOINTS.ENROLMENTS_REPORT)).data,

  /** Every learner in every certification with a RAG state (Certification status report). */
  getCertificationReport: async (): Promise<CertificationReportRow[]> =>
    (await axiosInstance.get<CertificationReportRow[]>(LEARNING_PATH_ENDPOINTS.CERTIFICATIONS_REPORT)).data,

  /** Completion editor: completion date (null = not complete / not certified) and, for certifications, the renewal date. */
  setLearnerCompletion: async (
    pathId: number | string, userId: number, body: { completion_date: string | null; expiry_date?: string | null },
  ): Promise<ProgramLearnerCompletionResult> =>
    (await axiosInstance.put<ProgramLearnerCompletionResult>(`${LEARNING_PATH_ENDPOINTS.LEARNER(pathId, userId)}/completion`, body)).data,

  /** The signed-in learner's own programs and certifications (My reports). */
  getMyReport: async (): Promise<ProgramEnrolmentReportRow[]> =>
    (await axiosInstance.get<ProgramEnrolmentReportRow[]>(LEARNING_PATH_ENDPOINTS.MY_REPORT)).data,

  /** End an assignment now: its learners who haven't completed are unenrolled. */
  unenrolAssignment: async (pathId: number | string, assignmentId: number | string): Promise<{ unenrolled: number }> =>
    (await axiosInstance.post<{ unenrolled: number }>(LEARNING_PATH_ENDPOINTS.ASSIGNMENT_UNENROL(pathId, assignmentId))).data,

  getEnrolmentMethods: async (pathId: number | string): Promise<ProgramEnrolmentMethod[]> =>
    (await axiosInstance.get<ProgramEnrolmentMethod[]>(LEARNING_PATH_ENDPOINTS.ENROLMENT_METHODS(pathId))).data,

  updateEnrolmentMethod: async (
    pathId: number | string,
    method: ProgramEnrolMethodType,
    body: { is_active: boolean; settings?: ProgramEnrolmentMethod['settings']; form_id?: number | null },
  ): Promise<ProgramEnrolmentMethod> =>
    (await axiosInstance.put<ProgramEnrolmentMethod>(LEARNING_PATH_ENDPOINTS.ENROLMENT_METHOD(pathId, method), body)).data,

  /** The learner joins: enrolled straight away, or their approval request starts. */
  enrol: async (pathId: number | string): Promise<{ status: 'enrolled' | 'pending'; instance_id: number | null }> =>
    (await axiosInstance.post<{ status: 'enrolled' | 'pending'; instance_id: number | null }>(LEARNING_PATH_ENDPOINTS.ENROL(pathId))).data,

  listLearners: async (
    pathId: number | string,
    params: { search?: string; limit?: number; offset?: number } = {},
  ): Promise<{ total: number; items: ProgramLearnerRow[] }> =>
    (await axiosInstance.get<{ total: number; items: ProgramLearnerRow[] }>(LEARNING_PATH_ENDPOINTS.LEARNERS(pathId), { params })).data,

  /** One learner's due date by hand (null = none), or back to the assignment rules. */
  updateLearnerDue: async (
    pathId: number | string,
    userId: number,
    body: { due_date?: string | null; use_rule?: boolean },
  ): Promise<void> => {
    await axiosInstance.patch(LEARNING_PATH_ENDPOINTS.LEARNER(pathId, userId), body);
  },

  unenrolLearner: async (pathId: number | string, userId: number): Promise<void> => {
    await axiosInstance.delete(LEARNING_PATH_ENDPOINTS.LEARNER(pathId, userId));
  },

  /** Remove one way a learner is enrolled (self / manager / approval / direct). */
  removeLearnerSource: async (pathId: number | string, userId: number, assignmentId: number): Promise<{ unenrolled: boolean }> =>
    (await axiosInstance.delete<{ unenrolled: boolean }>(LEARNING_PATH_ENDPOINTS.LEARNER_SOURCE(pathId, userId, assignmentId))).data,

  /** Open a certified learner's renewal window now (moves them to the recertification path and resets its courses for them). */
  openRenewal: async (pathId: number | string, userId: number): Promise<{ opened: boolean; already_open: boolean; courses_reset: number; stage: 'original' | 'recert' }> =>
    (await axiosInstance.post(`${LEARNING_PATH_ENDPOINTS.LEARNER(pathId, userId)}/open-renewal`)).data,

  /** Remind chosen learners or an audience; dry_run returns only the counts. */
  remind: async (
    pathId: number | string,
    body: {
      user_ids?: number[]; audience?: 'all' | 'not_completed' | 'overdue';
      subject?: string; message?: string; cc_manager?: boolean; dry_run?: boolean;
    },
  ): Promise<{ recipients: number; sent: number; failed: number; counts: Record<'all' | 'not_completed' | 'overdue', number> }> =>
    (await axiosInstance.post(LEARNING_PATH_ENDPOINTS.REMIND(pathId), body)).data,

  /** The learner unenrols themselves (self-enrolled, when allowed). */
  leave: async (pathId: number | string): Promise<void> => {
    await axiosInstance.post(LEARNING_PATH_ENDPOINTS.LEAVE(pathId));
  },
  getMyProgress: async (): Promise<LearningPathProgress[]> =>
    (await axiosInstance.get<LearningPathProgress[]>(LEARNING_PATH_ENDPOINTS.MY_PROGRESS)).data,
  getUserProgress: async (userId: number | string): Promise<LearningPathProgress[]> =>
    (await axiosInstance.get<LearningPathProgress[]>(LEARNING_PATH_ENDPOINTS.USER_PROGRESS(userId))).data,

  getUserCycles: async (userId: number | string, pathId: number | string): Promise<CertificationCycle[]> =>
    (await axiosInstance.get<CertificationCycle[]>(LEARNING_PATH_ENDPOINTS.USER_CYCLES(userId, pathId))).data,

  getLearnerView: async (pathId: number | string): Promise<LearningPathLearnerView> =>
    (await axiosInstance.get<LearningPathLearnerView>(LEARNING_PATH_ENDPOINTS.LEARNER_VIEW(pathId))).data,

  getProgramsReport: async (): Promise<ProgramReportRow[]> =>
    (await axiosInstance.get<ProgramReportRow[]>(LEARNING_PATH_ENDPOINTS.PROGRAMS_REPORT)).data,

  // ── Program (learning path) ratings ──────────────────────────────────────
  rateLearningPath: async (id: number | string, data: LearningPathRatingCreate): Promise<LearningPathRatingItem> =>
    (await axiosInstance.post<LearningPathRatingItem>(LEARNING_PATH_ENDPOINTS.RATE(id), data)).data,

  getLearningPathRatings: async (id: number | string, offset = 0, limit = 20): Promise<PaginatedLearningPathRatingsResponse> => {
    const query = buildQueryString({ offset, limit });
    return (await axiosInstance.get<PaginatedLearningPathRatingsResponse>(`${LEARNING_PATH_ENDPOINTS.RATINGS(id)}${query}`)).data;
  },

  deleteLearningPathRating: async (id: number | string, ratingId: number | string): Promise<void> => {
    await axiosInstance.delete(LEARNING_PATH_ENDPOINTS.DELETE_RATING(id, ratingId));
  },

  // ── Program (learning path) resources ─────────────────────────────────────
  listResources: async (id: number | string): Promise<LearningPathResource[]> =>
    (await axiosInstance.get<LearningPathResource[]>(LEARNING_PATH_ENDPOINTS.RESOURCES(id))).data,

  createResource: async (id: number | string, data: LearningPathResourceCreate): Promise<LearningPathResource> =>
    (await axiosInstance.post<LearningPathResource>(LEARNING_PATH_ENDPOINTS.RESOURCES(id), data)).data,

  updateResource: async (
    id: number | string,
    resourceId: number | string,
    data: LearningPathResourceUpdate,
  ): Promise<LearningPathResource> =>
    (await axiosInstance.put<LearningPathResource>(LEARNING_PATH_ENDPOINTS.RESOURCE(id, resourceId), data)).data,

  deleteResource: async (id: number | string, resourceId: number | string): Promise<void> => {
    await axiosInstance.delete(LEARNING_PATH_ENDPOINTS.RESOURCE(id, resourceId));
  },
};